import { IMenuCommand } from "../menu/commands/menu-command";
import { GameConfig } from "../config/game-config";
import { MenuActionType } from "../menu/menu-action-type";
import { Menu } from "../menu/menu";
import { Assets } from "./resources";
import { table } from "./table";
import { Mouse } from "../input/mouse";
import { IAssetsConfig } from "../types/game-config-type";
import { TournamentMessageEnum } from "../types/tournament-message-enum";
import { GameItem } from "../types/game-item";
import { GameTypeEnum } from "../types/game-type-enum";
import { AbstractPlayer } from "../types/abstract-player";
import { HitResultEnum } from "../types/hit-result-enum";
import { RoomItem } from "../types/room-item";
import { GameActionEnum } from "../types/game-action-enum";
import { GameStage } from "../types/game-stage-enum";
import websocketHandler from "../protocol/websocket-handler";
import { WebsocketMessageType } from "../protocol/websocket-message-type";
import { EnterRoomMessage } from "../protocol/messages/enter-room-message";
import { ChatMessage } from "../protocol/messages/chat-message";
import { store } from "../stores";
import { MutationType } from "../stores/mutation-types";
import { Player } from "./player";
import { ListPlayersMessage } from "../protocol/messages/list-players-message";
import { state } from "../stores/state";
import { Vector2 } from "../math/vector2";
import { Game8Pool } from "./game-8pool";
import { Game8PoolUK } from "./game-8pool-uk";
import { Game9Pool } from "./game-9pool";
import { GameSnooker } from "./game-snooker";
import { GamePractice } from "./game-practice";
import { WagerTypeEnum } from "../types/wager-type-enum";
import Web3 from "web3";
import AppConfig from "../config/app-config";
import WagerGameJson from "../../BoozaWagerGame.json"
import mixpanel from 'mixpanel-browser';
import { PassBackMessage } from "../protocol/messages/pass-back-message";
import { GameEventBus } from "./game-event-bus";
import { GameSocketClient } from "../protocol/game-socket-client";
// The following message types are now only constructed inside
// protocol/game-socket-client.ts, which GameApp delegates its send*()
// methods to — see that file's doc comment. Removed from here:
// ExitRoomMessage, NewGameMessage, AcceptGameMessage, ExitGameMessage,
// HitCueBallMessage, CuePositionMessage, HitDirectionMessage,
// HitPointMessage, HitResultMessage, TournamentSignupMessage,
// TournamentAcceptMessage, SynchronizeGameMessage, GameMessage,
// UserUpdateDisplayNameMessage.

declare let window: any;
const sounds: IAssetsConfig = GameConfig.sounds;

/**
 * This is the game base class.
 *
 * Many of the functions in this class act as a wrapper 
 * for the websocket functionality.
 */
export class GameApp {
  public static MISSED_TURN_LIMIT = 3;

  public winner: boolean;
  public newCredits: number;
  public selectedRoom: RoomItem;
  public gameItem: GameItem;
  public wagerMode: WagerTypeEnum = WagerTypeEnum.Wallet;

  // Generic pub/sub (game-event-bus.ts) and outbound-message building
  // (protocol/game-socket-client.ts) used to be implemented directly on
  // this class; both are now separate, focused modules this class
  // delegates to — see their doc comments for why.
  private eventBus = new GameEventBus();
  private socketClient = new GameSocketClient({
    getPlayer: () => this.player,
    getGameStage: () => this.gameStage,
  });

  private missedTurnCount: number;
  private missedTurnFoul: boolean;

  private gameStage: GameStage = GameStage.LoggedOut;
  private gameKind: string;

  private game: any;
  private player: Player;

  private synchro: boolean;
  private checkSum: number;

  // The smart contract variables.
  private web3: Web3;
  private addresses: string[];
  private contract: any;

  private menuActionsMap: Map<MenuActionType, IMenuCommand>;
  private isLoading: boolean;
  private inGame: boolean;

  // The prices of the cryptos used for wagers from the coin Gekio API.
  public cryptoPricesJSON: any;

  // The menu is re-drawn in index.vue when the window is resized.
  public menu: Menu = new Menu();

  // This is used to stop the main game animation.
  public requestId: any;

  private synchronizeOpponentTimeout: any;
 
  public getGameStage(): GameStage {
    return this.gameStage;
  }

  public setGameStage(theGameStage: GameStage): void {
    this.gameStage = theGameStage;
  }

  public getGameKind(): string {
    return this.gameKind;
  }

  public setGameKind(theGameKind: string): void {
    this.gameKind = theGameKind;
  }

  public getInGame(): boolean {
    return this.inGame;
  }

  public setInGame(isInGame: boolean): void {
    this.inGame = isInGame;
  }

  public getMissedTurnFoul(): boolean {
    return this.missedTurnFoul;
  }

  public setMissedTurnFoul(hasMissedTurn: boolean): void {
    this.missedTurnFoul = hasMissedTurn;
  }

  /**
   * Gets the game player.
   * This is used to identify browser the player and for the prectice game.
   * 
   * @returns - the game player
   */
  public getPlayer(): Player {
    return this.player;
  }

  /**
   * Determine if it's the player turn.
   * 
   * @returns - true of its the current player turn false if not
   */
  public isPlayerTurn(): boolean {
    return this.game.currentTurnPlayer.playerId === this.player.playerId;
  }

  /**
   * Sets the game player.
   * This is used to identify the player and for the practice game.
   * 
   * @param thePlayer - the game player
   */
  public setPlayer(thePlayer: Player): void {
    this.player = thePlayer;
  }

  /**
   * Update the stored player details in the "game" class.
   */
  public setPlayers(player1: Player, player2: Player): void {
    // Set the players in the billard game class.
    this.game.setPlayers(player1, player2);
  }

  public getGameItem(): GameItem {
    return this.gameItem;
  }

  public getBillardGame(): any {
    return this.game;
  }
  
  /**
   * This is to initialize the game.
   */
  public async init(): Promise<void> {
    // Enabling the debug mode flag is useful during implementation,
    // but it's recommended it should be removed for production
    mixpanel.init('bfb467f8c2f66b364e2718af3c62e8ed', {debug: true}); 
    
    Mouse.init();
    table.init();
    
    await Assets.loadGameAssets();

    // This constructor will depend on the game type.
    if (this.getGameKind() === 'POOL8') {
      this.game = new Game8Pool();
    } else if (this.getGameKind() === 'POOL9') {
      this.game = new Game9Pool();
    } else if (this.getGameKind() === 'POOL8UK') {
      this.game = new Game8PoolUK();
    } else if (this.getGameKind() === 'SNOOKER') {
      this.game = new GameSnooker(false);
    } else if (this.getGameKind() === 'SNOOKER6') {
      this.game = new GameSnooker(true);
    } else { // practice game 
      this.game = new GamePractice();
    }
    
    this.requestId = undefined;
    
    if (this.getGameKind() === 'PRACTICE' || this.getGameKind() === undefined) {
      this.startAnimate(); // start the game animation
    } else {
      // Draw the menu.
      // The menu is re-drawn in index.vue when the window is resized.
      this.initMainMenu();
      this.menu.active = true;
      this.menu.draw();
    }
  }

  /**
   * The start point for the practice game.
   */
  public startPractice(): void {
    let player = new Player();
    player.score = 0;
    player.playerId = this.getPlayer().playerId;
   
    // set the player in the store
    store.commit(MutationType.PLAYER1, player);

    // clear the player room list
    store.commit(MutationType.PLAYER_LIST, []);

    // add the player to the practice room
    store.commit(MutationType.USER_ADDED, this.getPlayer().player);
    
    table.gameType = this.getGameKind();
    this.game = new GamePractice();

    this.menu.active = false;
    this.inGame = true;

    this.game.initGame(); // initialise the practice game engine

    // set the player in the game
    this.game.player = player;

    const chatMessage: ChatMessage = {
      msgType: WebsocketMessageType.CHAT_MSG,
      playerId: 'STATUS',
      message: 'New game started',
    }

    store.commit(MutationType.CHAT_MESSAGE, chatMessage);

    mixpanel.track('Start Practice Game', {
      'Game Kind': table.gameType,
      'Player': player,
    });

    // Emit "start game" event to the Vue.js front end.
    this.fireEvent("START_GAME");
  }

  /**
   * The start point for the 2-player games.
   */
  public start(): void {
    this.menu.active = false;
    this.setInGame(true);

    // These are needed for the game status component.
    store.commit( MutationType.PLAY_TURN, this.game.players[this.game.currentPlayerIndex].playerId);
    
    store.commit(MutationType.PLAYER1, this.game.players[0]);
    store.commit(MutationType.PLAYER2, this.game.players[1]);

    table.gameType = this.getGameKind();

    this.game.initGame(); // initialise the specific billiard game engine

    this.requestId = undefined;

    mixpanel.track('Start Game', {
      'Game Kind': table.gameType,
      'Player 1': this.game.players[1],
      'Player 2': this.game.players[2],
      'Wager': this.gameItem.wager > 0 ? true : false,
    });

    this.startAnimate(); // start the game animation

    // Emit "start game" event to the Vue.js front end.
    this.fireEvent("START_GAME");
  }

  /**
   * Connect the wallet.
   */
  private async connectWalletAndLoadContract(network: string): Promise<any> {
    if (!this.addresses) {
      console.log("Getting Web3...");     

      if (window.ethereum) { // check for MetaMask
        this.web3 = new Web3(window.ethereum);
        await window.ethereum.enable(); // ask permission to connect to MetaMask.
      } else if (window.web3) { // the user has an old MetaMask or other wallet
        this.web3 = new Web3(window.web3.currentProvider);
      }

      this.addresses = await this.web3.eth.getAccounts(); 
      console.log("this.addresses[0]", this.addresses[0]);
    }  
      
    // the ABI file is a simple JSON file where the object key is "abi"
    // the ABI file defines the public methods of the smart contract for TypeScript
    //@ts-ignore
    this.contract = new this.web3.eth.Contract(WagerGameJson.abi, AppConfig.SMART_CONTRACT_ADDRESS);
    console.log("contract", this.contract);   
  }

  /**
   * Initiate the wager game.
   * 
   * @param player2  
   * @param gameType 
   * @param timeLimit 
   * @param wager 
   */
  public async initiateWagerGame(player2: AbstractPlayer, gameType: number,
    timeLimit: number, network: string, wager): Promise<any> {
      
    await this.connectWalletAndLoadContract(network); 
    
    try {
      // execute the smart contract on the blockchain
      const reciept = await this.contract.methods.initiateWagerGame(this.player.playerId, player2.playerId).send({
        from: this.addresses[0],
        value: Web3.utils.toWei(wager.toString(), 'ether'), // get wager walue in wei
      });
       
      console.log("reciept", reciept);
      
      const transferValidityStatus = reciept.status;

      if (transferValidityStatus) {
        const newGame: GameItem = {
          player2: player2,
          gameType: gameType,
          timeLimit: timeLimit,
          network: network,
          wager: wager,
        };
        this.createNewGameRequest(newGame);
      }
    } catch (e) {
      console.error(e);
    } finally {
      // TODO clean up
    } 
  }

  /**
   * Accept the wager game.
   * 
   * @param initiatorId 
   * @param wager 
   */
  public async acceptWagerGame(initiatorId: string, wager: number, network: string): Promise<any> {
    
    await this.connectWalletAndLoadContract(network); 

    try {
      // execute the smart contract on the blockchain
      const reciept = await this.contract.methods.acceptWagerGame(initiatorId, this.player.playerId).send({
        from: this.addresses[0],
        value: Web3.utils.toWei(wager.toString(), 'ether'), // get wager walue in wei
      });

      console.log("reciept", reciept);
        
      const transferValidityStatus = reciept.status;

      if (transferValidityStatus) {
        this.sendAcceptGame(true);
      } else {
        this.sendAcceptGame(false);
      }
    } catch (e) {
      console.error(e);
      this.sendAcceptGame(false);
    } finally {
      // TODO clean up
    } 
  }

  /**
   * This is used for a custom event system.
   *
   * It enables events triggered when messages are recieved
   * so that events can be fired on the Vue.js front end.
   *
   * This function listens for the event.
   * 
   * @see fireEvent function for more information. 
   */
  public onEvent(event: string, callback: any): void {
    this.eventBus.on(event, callback);
  }

  /**
   * This is used for a custom event system.
   *
   * It enables events triggered when messages are recieved
   * so that events can be fired on the Vue.js front end.
   *
   * This function fires the event.
   * 
   * All of the events are triggered in the "game.ts" class
   * or the game implementations.
   *
   * Currently the application has the following events:
   * 
   * START_GAME - This is triggered at the start of a game.
   * 
   * HIT_CUE_BALL - This is triggered when the cue ball is hit
   * by the player in a 2 player game.
   * 
   * OPPONENT_HIT_CUE_BALL - This is triggered when the cue 
   * ball is hit by the opponent in a 2 player game.
   * 
   * OPPONENT_POSITION_CUE_BALL - This is triggered when the cue 
   * ball is positioned by the opponent in a 2 player game.
   * 
   * OPPONENT_ADJUST_HIT_POSITION - This is triggered when the hit
   * position is set by the oppoenet in a 2 player game.
   * 
   * PLAYER_CHANGE - This is triggered when the current player turn is switched
   * to thr opponent in a 2 player game.
   * 
   * OK - This is triggered when the current player shoots again in a two payer game.
   * 
   * END_TURN - This is triggered when the turn is ended by the
   * player and the players are synchronised in a 2 player game.
   * 
   * PASS_BACK - This is triggered when a player passes the turn back to the opponent.
   * 
   * END_GAME - This is triggered at the end of a game.
   * 
   * CONCEDE_GAME - This is triggered when a player chooses to concede the game.
   * 
   * OPEN_DONATE, OPEN_HELP, OPEN_SETTINGS - Opens modals.
   * 
   * Also @see onEvent related function for more information. 
   */
  public fireEvent(event: string, payload?: any): void {
    this.eventBus.fire(event, payload);
  }

  public connectWebsocket(playerId: string): void {
    this.socketClient.connectWebsocket(playerId);
  }

  /*-- BEGIN - THESE METHODS ARE COPIED FROM THE JAVA APPLET --*/
  /*-- THESE METHODS ARE INTERGRATED INTO THIS VERSION OF THE GAME --*/
  /*-- THE JAVA LOGIC IS MOSTLY FOLLOWED AND CONVERTED TO TYPESCRIPT --*/

  public selectRoom(room: RoomItem): void {  
    this.selectedRoom = room;

    const message: EnterRoomMessage = {
      msgType: WebsocketMessageType.ENTER_ROOM,
      sender: this.player.player,
      room,
    };

    websocketHandler.send(message);
  }

  public sendChatMessage(msg: string): void {
    this.socketClient.sendChatMessage(msg);
  }

  public updateDisplayName(player: AbstractPlayer): void {
    this.socketClient.updateDisplayName(player);
  }

  public leaveRoom(): void {
    this.socketClient.leaveRoom();
  }

  /**
   * This method is invoked when a user clicks the button on the modal
   * to create a new game.
   *
   * @param game - the game item
   */
  public createNewGameRequest(game: GameItem): void {
    this.socketClient.createNewGameRequest(game);
  }

  public gameInviteStatus(inviteSent: string): void {
    const isInviteSent = inviteSent === "true" ? true : false;

    // if an invite was sent to opponent show wait for the response dialog
    if (isInviteSent) {
      this.setGameStage(GameStage.InvitationSent);
      store.commit(MutationType.WAIT_FOR_OPPONENT_MODAL, true);
    } else {
      this.closeAllModals();
      this.setGameStage(GameStage.InRoom);
      this.initRoom(); // go back to the room and reinitialise the room data
      this.openInfoModal("Player is busy, please try later..");
    }
  }

  public tournamentStatus(status: TournamentMessageEnum): void {
    switch (status) {
      case TournamentMessageEnum.NOT_REGISTERED:
        this.openInfoModal("You are not registered.");
        break;
      case TournamentMessageEnum.ALREADY_STARTED:
        this.openInfoModal("This tournament has already started.");
        break;
      case TournamentMessageEnum.PRIVATE:
        this.openInfoModal("This tournament is private.");
        break;
      case TournamentMessageEnum.FULL:
        this.openInfoModal("This tournament is now full.");
        break;
      case TournamentMessageEnum.INSUFFIENT_CREDIT:
        this.openInfoModal("You don't have enough credits to join.");
        break;
      case TournamentMessageEnum.ALREADY_JOINED:
        this.openInfoModal("You have already joined this tournament.");
        break;
      case TournamentMessageEnum.UNKNOWN:
        this.openInfoModal("You cannot join this tournament.");
        break;
      case TournamentMessageEnum.MOVED:
        this.openInfoModal("You have been moved to the tournament room. Please wait for the tournament to start.");
        break;
      case TournamentMessageEnum.WAIT:
        this.openInfoModal("No opponent for this round. Please wait.");
        break;
      case TournamentMessageEnum.LOST_GAME:
        this.openInfoModal("You are eliminated.");
        break;
      case TournamentMessageEnum.WON_GAME:
        this.openInfoModal("You won. Please wait for the next round.");
        break;
      case TournamentMessageEnum.WINNER:
        this.openInfoModal("You are the tournament winner!");
        break;
      default:
        break;
    }
  }

  /**
   * This method gets called when the application receives a new game invitation.
   *
   * @param newGame - object holds game parameters
   * @param continuation - this is "true" if the game is stated from the "Play again?" button
   */
  public newGameInvite(newGame: GameItem, continuation: string): void {
    const isContinuation = continuation === "true" ? true : false;

    let hasEnoughCredits = true; // used for credit wager games to validate the game

    if (GameTypeEnum[GameTypeEnum[newGame.gameType]] == GameTypeEnum.Wager && this.wagerMode === WagerTypeEnum.Credits) {
      if (
        this.player.player.credits -
          (this.player.player.unclearedCredits + this.newCredits) <
        newGame.wager
      ) {
        hasEnoughCredits = false;
      }
    }

    if ((store.state.settings.blockWagerGameChallenges && GameTypeEnum[GameTypeEnum[newGame.gameType]] == GameTypeEnum.Wager)
      || store.state.settings.blockAllGameChallenges) {
      store.commit(MutationType.GAME_MESSAGE, `${newGame.player1} game inviatation has been blocked`);
      this.sendAcceptGame(false);
    } else {
      let resultTxt = "";

      if (!isContinuation) {
        if (newGame.gameType !== GameTypeEnum.Tournament) {
          // the old Java code checked the room
          resultTxt = newGame.player1 + " invites you to play.";
        } else {
          // this is displayed in a tournament room
          resultTxt = "Do you want to play?";
        }
      } else {
        if (this.winner != null) {
          if (this.winner === true) {
            resultTxt = "You win.";
          } else {
            resultTxt = "You lose.";
          }
        } else {
          resultTxt = "You lose.";
        }

        resultTxt += " Play again?";

        if (GameTypeEnum[GameTypeEnum[newGame.gameType]] == GameTypeEnum.Wager && this.wagerMode === WagerTypeEnum.Credits) {
          if (this.winner != null) {
            // Update credits if it's a non-tournament wager game (client session).
            if (this.winner === true) {
              this.player.player.credits = this.player.player.credits + newGame.wager;
              this.newCredits += newGame.wager;
            } else {
              this.player.player.credits = this.player.player.credits - newGame.wager;
            }
          } else {
            this.player.player.credits = this.player.player.credits - newGame.wager;
          }
        }

        // Emit "end game" event to the Vue.js front end.
        this.fireEvent("END_GAME");
      }

      this.setGameStage(
        isContinuation ? GameStage.GameContinueConfirm : GameStage.GameConfirmation
      );

      // open the invitation request modal
      store.commit(MutationType.INVITATION_REQUEST_MODAL, true);
      store.commit(MutationType.INVITATION_REQUEST_GAME, newGame);
      store.commit(MutationType.INVITATION_REQUEST_TXT, resultTxt);
    }
  }

  public sendAcceptGame(accept: boolean): void {
    // send game accept or cancel request
    this.socketClient.sendAcceptGame(accept);
  }

  public discardGame(player: AbstractPlayer): void {
    // hide create game confirmation dialog
    this.closeAllModals();
  
    const stage = this.getGameStage();

    switch (stage) {
      case GameStage.InvitationSent:
        this.openInfoModal("Player unable or declined to play.");
        break;
      case GameStage.GameConfirmation:
        this.openInfoModal("Player cancelled invitation.");
        break;
      case GameStage.GameContinueConfirm:
        this.openInfoModal("Player declined another game.");
        break;
      case GameStage.BeforeWatch:
        this.openInfoModal("Game over!");
        break;
      default:
        break;
    }

    this.initRoom(); // go back to the room and reinitialise the room data
  }

  /**
   * This message is sent by the server to both players connected to the game.
   * 
   * In the Java version it is also sent to all the observers of the game. 
   *
   * @param newGame
   * @param initiator
   */
  public startGame(newGame: GameItem, initiatorIndex: number, seed1: number, seed2: number): void {
    console.log("newGame", newGame);

    // close all modals
    this.closeAllModals();

    // clear the room chat
    store.commit(MutationType.CHAT_CLEAR);

    // Set the current player turn to the initiator.
    // It appears that index 0 is the initiator and index 1 is the opponent.
    this.game.currentTurnPlayerIndex = initiatorIndex;

    // The "newGame" player object only holds the player ID string.
    // It seems like it should hold the whole "AbscractPlayer" object.
    // Instead we get the "AbstractPlayer" object from the room store.
    const player1Id: any = newGame.player1;
    const player2Id: any = newGame.player2;

    // Get the game abstract player objects from the room.
    const player1: AbstractPlayer = this.getPlayerFromRoom(player1Id);
    const player2: AbstractPlayer = this.getPlayerFromRoom(player2Id);

    // Store the abstract players in the new game.
    newGame.player1 = player1;
    newGame.player2 = player2;

    // set the game item
    this.gameItem = newGame;

    // Create player 1.
    const gamePlayer1 = new Player();
    gamePlayer1.score = 0;
    gamePlayer1.playerId = player1.playerId;
    gamePlayer1.enablePassback = false;
    gamePlayer1.player = player1; // the "AbstractPlayer"

    // Create player 2.
    const gamePlayer2 = new Player();
    gamePlayer2.score = 0;
    gamePlayer2.playerId = player2.playerId;
    gamePlayer2.enablePassback = false;
    gamePlayer2.player = player2; // the "AbstractPlayer"

    // Set the players in the game application class.
    this.setPlayers(gamePlayer1, gamePlayer2);
    
    // clear the room players from the players list
    store.commit(MutationType.PLAYER_LIST, []);

    // Add the abstract players to the game room list.
    store.commit(MutationType.USER_ADDED, player1);
    store.commit(MutationType.USER_ADDED, player2);

    this.missedTurnCount = 0;
    this.winner = false; 

    this.start(); // here we start a new game

    this.gameStage = GameStage.GameInProgress;
  }

  public sendCueBallPosition(cuePosition: Vector2): void {
    this.socketClient.sendCueBallPosition(cuePosition);
  }

  public sendHitPoint(pointX: number, pointY: number): void {
    this.socketClient.sendHitPoint(pointX, pointY);
  }

  public sendHitDirection(direction: Vector2): void {
    this.socketClient.sendHitDirection(direction);
  }

  /**
   * This sends a message has to the Java server side as a game message.
   * This is actually just a wrapper around the web socket sender.
   *
   * @param message - the message to be sent to the server
   */
  public sendGameMessage(message: any): void {
    this.socketClient.sendGameMessage(message);
  }

  public sendHitCueBall(direction: number, power: number,
    hitPosition: Vector2, ballPos: Vector2): void {
    this.socketClient.sendHitCueBall(direction, power, hitPosition, ballPos);
  }

  /**
   * This is called after the player exits or leaves the game.
   */
  public initRoom(): void {
    if (this.requestId !== undefined) {
      this.stopAnimate(); // draw the menu 
    }

    this.setGameStage(GameStage.InRoom);

    // clear the room chat
    store.commit(MutationType.CHAT_CLEAR);

    // get the players for the selected game room
    const message: ListPlayersMessage = {
      msgType: WebsocketMessageType.LIST_PLAYERS_MESSAGE,
      sender: this.player.player,
    };

    try {
      websocketHandler.send(message);
    } catch (e) {
      console.error(e);
    }
  }

  /**
   * This is called after the cue ball is hit.
   * The the hit result then needs to be computed.
   * @param result
   */
  public sendHitResult(result: HitResultEnum): void {
    // On the server there is "player1" who is the game initiator and "player2" who is the opponent.
    // I think "score1" should relate to "player1" and "score2" should relate to "player2".
    const scores: number[] = this.game.getScores();

    let wager: boolean = false;

    if ((result === HitResultEnum.WIN_GAME ||
      result === HitResultEnum.WIN_GAME_GOLDEN_BREAK ||
      result === HitResultEnum.WIN_GAME_RUNOUT ||
      result === HitResultEnum.WIN_GAME_SEVEN_BALLING ||
      result === HitResultEnum.WIN_GAME_CENTURY ||
      result === HitResultEnum.WIN_GAME_SYNCHRONIZE_ERROR ||
      result === HitResultEnum.LOST_GAME ||
      result === HitResultEnum.LOST_GAME_ABANDOND ||
      result === HitResultEnum.LOST_GAME_FOULS) &&
      GameTypeEnum[GameTypeEnum[this.gameItem.gameType]] == GameTypeEnum.Wager &&
      this.wagerMode === WagerTypeEnum.Wallet) {
      wager = true; // this sets the credits on the server side
    }

    if (this.game.currentTurnPlayer.playerId === this.player.playerId) {
      if (result === HitResultEnum.WIN_GAME ||
        result === HitResultEnum.WIN_GAME_GOLDEN_BREAK ||
        result === HitResultEnum.WIN_GAME_RUNOUT ||
        result === HitResultEnum.WIN_GAME_SEVEN_BALLING ||
        result === HitResultEnum.WIN_GAME_CENTURY ||
        result === HitResultEnum.WIN_GAME_SYNCHRONIZE_ERROR) {
        this.winner = true;
      } else if (result === HitResultEnum.LOST_GAME ||
        result === HitResultEnum.LOST_GAME_ABANDOND ||
        result === HitResultEnum.LOST_GAME_FOULS) {
        this.winner = false;
      }
    } else if (this.game.nextTurnPlayer.playerId === this.player.playerId) {
      if (result === HitResultEnum.WIN_GAME ||
        result === HitResultEnum.WIN_GAME_GOLDEN_BREAK ||
        result === HitResultEnum.WIN_GAME_RUNOUT ||
        result === HitResultEnum.WIN_GAME_SEVEN_BALLING ||
        result === HitResultEnum.WIN_GAME_CENTURY ||
        result === HitResultEnum.WIN_GAME_SYNCHRONIZE_ERROR) {
        this.winner = false;
      } else if (result === HitResultEnum.LOST_GAME ||
        result === HitResultEnum.LOST_GAME_ABANDOND ||
        result === HitResultEnum.LOST_GAME_FOULS) {
        this.winner = true;
      }
    }

    this.game.lastHitResult = result; // remember last result

    this.socketClient.sendHitResultMessage(
      result,
      // this has values of 0 and 1 on the server
      this.game.currentTurnPlayerIndex,
      // the scores are set here for convenience
      scores[0],
      scores[1],
      wager,
      this.gameItem.network,
    );

    // if it's a credit wager game update the wager
    if (GameTypeEnum[GameTypeEnum[this.gameItem.gameType]] === GameTypeEnum.Wager && this.wagerMode === WagerTypeEnum.Credits) {
      if (this.winner) {
        // update the wager in the client session
        this.player.player.credits = this.player.player.credits + this.gameItem.wager;
      } else {
        // update the wager in the client session
        this.player.player.credits = this.player.player.credits - this.gameItem.wager;
      }
    }
  }

  public sendLeaveGameMessage(): void {
    let wager: boolean = false;

    if (GameTypeEnum[GameTypeEnum[this.gameItem.gameType]] == GameTypeEnum.Wager &&
      this.wagerMode === WagerTypeEnum.Wallet) {
      wager = true;
    }

    this.socketClient.sendExitGameMessage(wager, this.gameItem.network);

    // if it's a credit wager game this player has lost the wager because he/she is leaving the game
    if (this.gameItem.gameType === GameTypeEnum.Wager && this.wagerMode === WagerTypeEnum.Credits) {
      this.player.player.credits = this.player.player.credits - this.gameItem.wager;
    }

    // Emit "end game" event to the Vue.js front end.
    this.fireEvent("END_GAME");

    this.closeAllModals();
    this.initRoom(); // go back to the room and re-initialise the room data
  }

  public opponentLeftGame(): void {
    if (GameTypeEnum[GameTypeEnum[this.gameItem.gameType]] === GameTypeEnum.Wager) {
      // update the wager in the client session
      if (this.wagerMode === WagerTypeEnum.Credits) {
        this.player.player.credits = this.player.player.credits + this.gameItem.wager;
        this.newCredits += this.gameItem.wager;
      } 
    }
  }

  public playerLeftGame(): void {
    if (GameTypeEnum[GameTypeEnum[this.gameItem.gameType]] === GameTypeEnum.Wager) {
      // update the wager in the client session
      if (this.wagerMode === WagerTypeEnum.Credits) {
        this.player.player.credits = this.player.player.credits - this.gameItem.wager;
        this.newCredits -= this.gameItem.wager;
      } 
    }
  }

  public userRemoved(player: AbstractPlayer): void {
    if (this.getGameStage() !== GameStage.GameInProgress) {
      // this is case when opponent disconnect from server, before game is accepted
      this.discardGame(player);
    } else {
      // Emit "end game" event to the Vue.js front end.
      this.fireEvent("END_GAME");

      // if this is one of the players end game and show information message
      this.openInfoModal(
        "You Win. Player " + player.playerId + " left the game"
      );

      // see if the wager needs to be adjusted for the client session
      this.opponentLeftGame();

      this.initRoom(); // go back to the room and reinitialise the room data
    }
  }

  public sendUserActionEvent(action: GameActionEnum): void {
    this.socketClient.sendUserActionEvent(action);
  }

  public sendPassBack(): void {
    this.socketClient.sendPassBack();
  }

  public passBack(message: PassBackMessage): void {

    // Emit "pass back" event to the Vue.js front end.
    this.fireEvent('PASS_BACK');

    this.game.lastHitResult = HitResultEnum.PLAYER_CHANGE;
    
    this.game.continueGame();

    Assets.playSound(sounds.paths.PASS_BACK, 1);

    const chatMessage: ChatMessage = {
      msgType: WebsocketMessageType.CHAT_MSG,
      playerId: 'GAME',
      message: `Player ${this.getBillardGame().nextTurnPlayer.playerId} passed the turn back to ${this.getBillardGame().currentTurnPlayer.playerId}.`,
    }

    store.commit(MutationType.CHAT_MESSAGE, chatMessage);

    this.game.players[0].enablePassback = false;
		this.game.players[1].enablePassback = false;

		// This this updates the status of the players in the front end.
		store.commit(MutationType.PLAYER1, this.game.players[0]);
    store.commit(MutationType.PLAYER2, this.game.players[1]);
  }

  public concedeGame(): void {
    this.sendLeaveGameMessage();
  }

  public sendSignupTournament(link: string): void {
    this.socketClient.sendSignupTournament(link);
  }

  /**
   * Open the join tournament modal. 
   * 
   * @param room - the tournament room
   * @param registered - the player registration status for the tournament 
   */
  public tournamentSignupResult(room: RoomItem, registered: boolean): void {
    this.openJoinTournamentModal(room, registered);
  }

  public sendAcceptTournament(roomId: string, registered: boolean): void {
    this.socketClient.sendAcceptTournament(roomId, registered);
  }

  public tournamentAcceptResult(room: RoomItem): void {
    try {
      if (this.gameStage === GameStage.GameInProgress) {
        this.sendLeaveGameMessage();
      }
    } catch (e) {
      console.error(e);
    }

    this.selectRoom(room);

    // This displays a message when the user is moved to the tournament room.
    this.tournamentStatus(TournamentMessageEnum.MOVED);
  }

  /**
   * This is called at the end of a tournament match to update the credits in
   * the client session.
   *
   * @param amount
   */
  public moifyPlayerCredits(amount: number): void {
    this.player.player.credits += amount;
  }

  public sendSynchronizeGameMessage(): void {
    this.socketClient.sendSynchronizeGameMessage();
  }

  public missedTurnLimit(): boolean {
    if (this.missedTurnFoul) {
      this.getBillardGame().missedTurnFoul(); // execute game missed turn functionality
      
      this.missedTurnFoul = false;

      this.sendGameMessage('Missed turn foul ' + this.missedTurnCount);
      
      if (this.getBillardGame().currentTurnPlayer.playerId === this.getPlayer().playerId) {
        this.missedTurnCount++;
      }
      
      if (this.missedTurnCount === GameApp.MISSED_TURN_LIMIT) {
        if (this.getBillardGame().currentTurnPlayer.playerId === this.getPlayer().playerId) {
          this.sendLeaveGameMessage();
        } else {
          // Emit "end game" event to the Vue.js front end.
          this.fireEvent('END_GAME'); 
          
          this.closeAllModals();
          this.initRoom(); // go back to the room and re-initialise the room data
        }

        return true;
      }

      return false;  
    }

    return false;
  }

  /**
   * On the Java version of the game this is done on the server side.
   * The Java version can have watchers that need to be synchronised.
   * With the browser version only the two players that are playing the 
   * game need to be synchronised.
   */
  public checkPlayersState(): void {
    this.checkSum++;
  
    if (this.checkSum === 2) {
      clearTimeout(this.synchronizeOpponentTimeout);
      this.setSynchronized(true);
    }
  }

  public isSynchronized(): boolean  {
		return this.synchro;
	}

	public setSynchronized(synchro: boolean): void {
    this.synchro = synchro;
  }

  public clearSynchro(): void {
    this.synchro = true;
    this.checkSum = 0;
  }

  public startSynhcronizeOpponentTimeout(): void {

    this.synchronizeOpponentTimeout = setTimeout(() => {   
      if (this.inGame) {
        // Emit "end game" event to the Vue.js front end.
        this.fireEvent('END_GAME');

        // end game and show information message
        this.openInfoModal(
          'You win. Cannot synchronized game. Game ended.'
        );

        // see if the wager needs to be adjusted for the client session
        this.opponentLeftGame();
      
        this.initRoom(); // go back to the room and reinitialise the room data 
      
        this.sendHitResult(HitResultEnum.WIN_GAME_SYNCHRONIZE_ERROR);
      }
    }, 20000); // run after 20 seconds
  }

  public errorGame(): void {
    // Emit "end game" event to the Vue.js front end.
    this.fireEvent('END_GAME');

    // end game and show information message
    this.openInfoModal(
      "You loose. Cannot synchronized game. Game ended."
    );

    // see if the wager needs to be adjusted for the client session
    this.playerLeftGame();

    this.initRoom(); // go back to the room and reinitialise the room data 
  }

  /*-- END - THESE METHODS ARE COPIED FROM THE JAVA APPLET --*/

  public openJoinTournamentModal(tournamentRoom: RoomItem, registered: boolean): void {
    store.commit(MutationType.JOIN_TOURNAMENT_ROOM, tournamentRoom);
    store.commit(MutationType.JOIN_TOURNAMENT_REG, registered);
    store.commit(MutationType.JOIN_TOURNAMENT_MODAL, true);
  }

  private openInfoModal(text: string): void {
    store.commit(MutationType.INFO_MODAL, true);
    store.commit(MutationType.INFO_TXT, text);
  }

  private closeAllModals(): void {
    store.commit(MutationType.INVITATION_REQUEST_MODAL, false);
    store.commit(MutationType.WAIT_FOR_OPPONENT_MODAL, false);
    store.commit(MutationType.INFO_MODAL, false);
    store.commit(MutationType.JOIN_TOURNAMENT_MODAL, false);
  }

  private getPlayerFromRoom(playerId: string): AbstractPlayer {
    const players: AbstractPlayer[] = state.playerList;
    //@ts-ignore
    const player: AbstractPlayer = players.find((ap) => {
      return ap.playerId === playerId;
    });
    return player;
  }

  public getEmptyPlayer(): Player {
    const player = new Player();
    player.score = 0;
    player.playerId = 'No Player';
    player.enablePassback = false;
    player.player;
    return player;
  }

  /*-- BEGIN - THESE FUNCTIONS ARE RELATED TO THE MAIN GAME LOOP AND MAIN MENU --*/

  /**
   * Start the main game animation.
   */
  public startAnimate(): void {
    this.update();
    this.draw();

    this.requestId = window.requestAnimationFrame(() => {
      // Recurseivly re-draw the main game table.
      this.startAnimate();
    });
  }

  /**
   * Stop the main game animation and draw the main menu.
   */
  public stopAnimate(): void {
    // Stop the main game animation.
    window.cancelAnimationFrame(this.requestId);
    this.requestId = undefined;
    table.clear();

    // Draw the menu.
    // The menu is re-drawn in index.vue when the window is resized.
    // The main menu is the "splash" screen.
    this.initMainMenu();
    this.menu.active = true;
    this.menu.draw();
  }

  private update(): void {
    if (this.isLoading) {
      return;
    }

    // The game stage is not set for the practice game. Also the practice game has no menu.
    // Therefore we need to ensure the game is always updated for the practice game.
    if ((this.gameKind !== 'PRACTICE' && this.getGameStage() === GameStage.GameInProgress) ||
      this.gameKind === 'PRACTICE') {
      this.game.update();
    } 
      
    Mouse.reset();
  }

  private draw(): void {
    if (this.isLoading) {
      return;
    }
    
    table.clear();
    this.game.draw();
  }  

  /**
   * The main menu is the "splash" screen.
   */
  private initMainMenu(): void {
    this.menu.init(this.menuActionsMap, GameConfig.mainMenu);
    this.menuActionsMap = new Map<MenuActionType, IMenuCommand>();
  }

  /*-- END - THESE FUNCTIONS ARE RELATED TO THE MAIN GAME LOOP --*/
}

export default new GameApp();
